import { supabase } from '../lib/supabase';

export const userJobPreferencesService = {
  // Get job preferences for a user in a specific company
  // Uses localStorage as fallback if Supabase hasn't loaded the schema yet
  async getPreferences(userId, companyId) {
    const storageKey = `user_job_prefs_${companyId}_${userId}`;
    const defaults = { showPaymentSection: true };

    try {
      const { data, error } = await supabase
        ?.from('user_job_preferences')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('company_id', companyId)
        ?.single();

      if (error) {
        // If 406 or PGRST error, use localStorage fallback
        if (error.code === 'PGRST116' || error.message?.includes('406')) {
          const stored = localStorage.getItem(storageKey);
          return stored ? JSON.parse(stored) : defaults;
        }
        // No rows found - return defaults
        if (error.code === 'PGRST116') {
          return defaults;
        }
        throw error;
      }

      return {
        id: data?.id,
        userId: data?.user_id,
        companyId: data?.company_id,
        showPaymentSection: data?.show_payment_section ?? true,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (err) {
      // Fallback to localStorage on any error
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : defaults;
    }
  },

  // Update or create job preferences for a user
  // Uses localStorage as fallback if Supabase hasn't loaded the schema yet
  async updatePreferences(userId, companyId, preferences) {
    const storageKey = `user_job_prefs_${companyId}_${userId}`;
    const prefsToSave = {
      showPaymentSection: preferences?.showPaymentSection ?? true
    };

    // Always save to localStorage as backup
    localStorage.setItem(storageKey, JSON.stringify(prefsToSave));

    try {
      const { data, error } = await supabase
        ?.from('user_job_preferences')
        ?.upsert({
          user_id: userId,
          company_id: companyId,
          show_payment_section: prefsToSave.showPaymentSection
        }, {
          onConflict: 'user_id,company_id'
        })
        ?.select()
        ?.single();

      if (error) {
        // If schema cache error, localStorage already has the data
        console.warn('User job preferences saved to localStorage (DB unavailable):', error.message);
        return prefsToSave;
      }

      return {
        id: data?.id,
        userId: data?.user_id,
        companyId: data?.company_id,
        showPaymentSection: data?.show_payment_section ?? true,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (err) {
      // localStorage already has the data as fallback
      console.warn('User job preferences saved to localStorage:', err.message);
      return prefsToSave;
    }
  },

  // Toggle the show payment section preference
  async toggleShowPaymentSection(userId, companyId) {
    // Get current preference
    const currentPrefs = await this.getPreferences(userId, companyId);
    const newValue = !currentPrefs.showPaymentSection;

    // Update with toggled value
    return this.updatePreferences(userId, companyId, {
      showPaymentSection: newValue
    });
  },

  // Delete preferences for a user (rarely needed, mainly for cleanup)
  async deletePreferences(userId, companyId) {
    const storageKey = `user_job_prefs_${companyId}_${userId}`;

    // Remove from localStorage
    localStorage.removeItem(storageKey);

    const { error } = await supabase
      ?.from('user_job_preferences')
      ?.delete()
      ?.eq('user_id', userId)
      ?.eq('company_id', companyId);

    if (error) throw error;
  }
};
